import React, { useEffect, useState, useCallback } from "react";
import { Table, Segmented, Select, DatePicker, Button, message, Tooltip } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy, faRotateLeft, faCopy } from "@fortawesome/free-solid-svg-icons";
import authApi from "../../../api/authApi";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;

const vnd = (v) => (v ? Number(v).toLocaleString("vi-VN") : "—");

const TAB_OPTIONS = [
    { value: "commission", label: "Top Hoa Hồng" },
    { value: "orders",     label: "Top Đơn Hàng" },
    { value: "withdraw",   label: "Top Rút Tiền" },
];

const LIMIT_OPTIONS = [10, 20, 50, 100, 200].map((n) => ({ value: n, label: `Top ${n}` }));

/** Rank column: gold / silver / bronze for top 3 */
const rankCol = {
    title: "#", key: "rank", width: 44, align: "center",
    render: (_, __, i) => {
        const cls = i === 0 ? "text-yellow-500 font-bold"
                  : i === 1 ? "text-content-3 font-bold"
                  : i === 2 ? "text-amber-700 font-bold"
                  : "text-content-3 text-xs";
        return <span className={cls}>{i + 1}</span>;
    },
};

/** User column with name + email sub-text (for tabs that include userInfo) */
const userInfoCol = {
    title: "Người dùng", key: "user", ellipsis: true,
    render: (_, r) => (
        <div className="leading-tight">
            <p className="font-medium text-sm">{r.userInfo?.name || `User #${r.userId}`}</p>
            <p className="text-xs text-content-3">{r.userInfo?.email || ""}</p>
            <p className="text-xs text-content-3">ID: {r.userId}</p>
        </div>
    ),
};

const COLUMNS = {
    commission: [
        rankCol,
        userInfoCol,
        { title: "Tổng hoa hồng", key: "total", align: "right", render: (_, r) => <span className="font-semibold text-blue-600">{vnd(r.totalUserCommission)}đ</span> },
        { title: "Đã duyệt",      key: "confirmed", align: "right", render: (_, r) => `${vnd(r.confirmedCommission)}đ` },
        { title: "Chờ duyệt",     key: "pending",   align: "right", render: (_, r) => `${vnd(r.pendingCommission)}đ` },
    ],
    orders: [
        rankCol,
        userInfoCol,
        { title: "Tổng đơn",  key: "total",     align: "right", render: (_, r) => <span className="font-semibold text-blue-600">{r.totalOrders ?? "—"}</span> },
        { title: "Đã duyệt", key: "confirmed", align: "right", render: (_, r) => r.confirmedOrders ?? "—" },
        { title: "Chờ duyệt",key: "pending",   align: "right", render: (_, r) => r.pendingOrders ?? "—" },
    ],
    withdraw: [
        rankCol,
        userInfoCol,
        { title: "Đã rút",   key: "withdraw", align: "right", render: (_, r) => <span className="font-semibold text-blue-600">{vnd(r.withdraw)}đ</span> },
        { title: "Đã duyệt",key: "paid",     align: "right", render: (_, r) => `${vnd(r.paid)}đ` },
        { title: "Thưởng",   key: "bonus",   align: "right", render: (_, r) => `${vnd(r.bonus)}đ` },
    ],
};

function TopUser() {
    const [tab, setTab]           = useState("commission");
    const [dateRange, setDateRange] = useState(null);
    const [limit, setLimit]       = useState(20);
    const [users, setUsers]       = useState([]);
    const [loading, setLoading]   = useState(false);

    /** Build query string; date range as Unix timestamps when present */
    const buildQuery = useCallback(() => {
        const params = { limit };
        if (dateRange) {
            params.startTime = dateRange[0].startOf("day").unix();
            params.endTime   = dateRange[1].endOf("day").unix();
        }
        return Object.entries(params).map(([k, v]) => `${k}=${v}`).join("&");
    }, [limit, dateRange]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            let list = [];
            const q = buildQuery();
            if (tab === "commission") {
                const { data } = await authApi.topUserMonth(q);
                list = data.userList || [];
            } else if (tab === "orders") {
                const { data } = await authApi.topUserOrders(q);
                list = data.userList || [];
            } else {
                const { data } = await authApi.topWithDraw(q);
                list = data.userList || [];
            }
            setUsers(list);
        } catch {
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, [tab, buildQuery]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const setQuickMonth = (offset) => {
        const base = offset === 0 ? dayjs() : dayjs().subtract(1, "month");
        setDateRange([base.startOf("month"), base.endOf("month")]);
    };

    const handleReset = () => { setDateRange(null); setLimit(20); setTab("commission"); };

    const handleCopyIds = () => {
        const ids = users.map((u) => u.userId).join("\n");
        navigator.clipboard.writeText(ids).then(() =>
            message.success(`Đã sao chép ${users.length} User ID`)
        );
    };

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-4">
            <h1 className="text-xl font-bold flex items-center gap-2">
                <FontAwesomeIcon icon={faTrophy} className="text-yellow-500" />
                Top User
            </h1>

            {/* Controls bar */}
            <div className="bg-surface border border-line rounded-lg p-3 flex flex-wrap gap-3 items-center shadow-sm">
                <Segmented
                    options={TAB_OPTIONS}
                    value={tab}
                    onChange={(v) => { setTab(v); setUsers([]); }}
                />

                <div className="flex gap-2 items-center flex-wrap ml-auto">
                    {/* Date controls – hidden for Top Rút Tiền (no date filter in that API) */}
                    {tab !== "withdraw" && (
                        <>
                            <Button size="small" onClick={() => setQuickMonth(0)}>Tháng này</Button>
                            <Button size="small" onClick={() => setQuickMonth(-1)}>Tháng trước</Button>
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                format="DD/MM/YYYY"
                                size="small"
                                allowClear
                                placeholder={["Từ ngày", "Đến ngày"]}
                                style={{ width: 230 }}
                            />
                        </>
                    )}
                    <Select
                        value={limit}
                        onChange={setLimit}
                        size="small"
                        style={{ width: 95 }}
                        options={LIMIT_OPTIONS}
                    />
                    <Tooltip title={`Sao chép ${users.length} User ID`}>
                        <Button
                            size="small"
                            icon={<FontAwesomeIcon icon={faCopy} />}
                            onClick={handleCopyIds}
                            disabled={users.length === 0}
                        />
                    </Tooltip>
                    <Button
                        size="small"
                        title="Đặt lại"
                        icon={<FontAwesomeIcon icon={faRotateLeft} />}
                        onClick={handleReset}
                    />
                </div>
            </div>

            <Table
                size="small"
                rowKey={(_, i) => i}
                dataSource={users}
                columns={COLUMNS[tab]}
                pagination={false}
                loading={loading}
                scroll={{ x: "max-content" }}
                rowClassName={() => "whitespace-nowrap"}
            />
        </div>
    );
}

export default TopUser;
